import { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useTenants } from './useTenants';
import { applyBranding } from './branding';

/**
 * Compact tenant dropdown for the top bar. Lists every tenant the agent may
 * access with a checkmark on the active one; selecting calls setTenant (which
 * persists + reloads). Renders nothing when the agent has only one tenant.
 */
export function TenantSwitcher() {
  const { availableTenants, activeTenant, setTenant } = useAuth();
  const { tenants } = useTenants(true, availableTenants);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Single (or zero) tenant: nothing to switch.
  if (availableTenants.length <= 1) return null;

  const active = tenants.find((t) => t.code === activeTenant);
  const activeLabel = active?.name || activeTenant || 'Tenant';

  function choose(code: string) {
    setOpen(false);
    if (code === activeTenant) return;
    const t = tenants.find((x) => x.code === code);
    if (t) applyBranding(t);
    setTenant(code);
  }

  return (
    <div ref={wrapRef} className="relative hidden sm:block">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium text-primary-100 hover:bg-primary-700 transition-colors max-w-[140px]"
        title="Switch tenant"
      >
        <span className="truncate">{activeLabel}</span>
        <ChevronDown className="h-3 w-3 flex-shrink-0 opacity-80" />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-52 rounded-md border border-gray-200 bg-white py-1 text-gray-900 shadow-lg z-[60]">
          {tenants.map((t) => (
            <button
              key={t.code}
              type="button"
              onClick={() => choose(t.code)}
              className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs hover:bg-gray-50"
            >
              <span
                className="h-2.5 w-2.5 flex-shrink-0 rounded-full"
                style={{ backgroundColor: t.primaryColor || '#2563eb' }}
              />
              <span className="min-w-0 flex-1 truncate">{t.name || t.code}</span>
              {t.code === activeTenant && (
                <Check className="h-3.5 w-3.5 flex-shrink-0 text-primary-600" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
